import React, { useState, useEffect } from 'react';
import { useAuth } from '../context/AuthContext';
import {
  User, Coins, Package, TrendingUp, Bell, Megaphone,
  Wallet, Zap, Leaf, Droplets, Award, Bot, BarChart3,
  Shield, Plus, Trash2, Edit2, X, UserCircle, Globe, Save,
  Ticket, MessageSquare, Send, ChevronDown, ChevronUp, AlertCircle
} from 'lucide-react';

interface ProfileData {
  urja_balance: number;
  ahant_balance: number;
  hydrogen_balance: number;
  rice_balance: number;
  nft_count: number;
  total_vehicles: number;
  total_battles: number;
  member_since: string;
}

interface Announcement {
  id: string;
  title: string;
  message: string;
  type: 'info' | 'success' | 'warning' | 'promo';
  target_type: 'broadcast' | 'user';
  target_user_name?: string;
  target_user_email?: string;
  created_at: string;
  expires_at?: string;
  is_active?: boolean;
}

interface UserInfo {
  ahn_id: string;
  name: string;
  email: string;
}

interface TicketType {
  id: string;
  subject: string;
  category: string;
  status: string;
  priority: string;
  created_at: string;
  updated_at: string;
  reply_count: number;
  last_reply_at?: string;
  user_name?: string;
  user_email?: string;
}

interface TicketReply {
  id: string;
  message: string;
  is_admin_reply: boolean;
  created_at: string;
  user_name: string;
}

const ADMIN_EMAIL = 'ahantravyuh@gmail.com';

const Profile: React.FC = () => {
  const { user, token } = useAuth();
  const [profileData, setProfileData] = useState<ProfileData | null>(null);
  const [nftCount, setNftCount] = useState<number>(0);
  const [garudLevel, setGarudLevel] = useState<number>(0);
  const [loading, setLoading] = useState(true);

  // Announcements state
  const [announcements, setAnnouncements] = useState<Announcement[]>([]);
  const [allAnnouncements, setAllAnnouncements] = useState<Announcement[]>([]);
  const [users, setUsers] = useState<UserInfo[]>([]);

  // Admin state
  const isAdmin = user?.email?.toLowerCase() === ADMIN_EMAIL.toLowerCase();
  const [showAdminPanel, setShowAdminPanel] = useState(false);
  const [editingAnnouncement, setEditingAnnouncement] = useState<Announcement | null>(null);
  const [newAnnouncement, setNewAnnouncement] = useState({
    title: '',
    message: '',
    type: 'info' as 'info' | 'success' | 'warning' | 'promo',
    target_type: 'broadcast' as 'broadcast' | 'user',
    target_user_email: '',
    expires_at: ''
  });
  const [announcementError, setAnnouncementError] = useState('');

  // Tickets state
  const [tickets, setTickets] = useState<TicketType[]>([]);
  const [allTickets, setAllTickets] = useState<TicketType[]>([]);
  const [selectedTicket, setSelectedTicket] = useState<TicketType | null>(null);
  const [ticketReplies, setTicketReplies] = useState<TicketReply[]>([]);
  const [newTicket, setNewTicket] = useState({
    subject: '',
    category: 'general',
    priority: 'medium',
    message: ''
  });
  const [newReply, setNewReply] = useState('');
  const [showNewTicketForm, setShowNewTicketForm] = useState(false);
  const [ticketFilter, setTicketFilter] = useState('all');

  useEffect(() => {
    fetchProfileData();
    fetchNFTCount();
    fetchGarudLevel();
    fetchAnnouncements();
    fetchTickets();
    if (isAdmin) {
      fetchAllAnnouncements();
      fetchUsers();
      fetchAllTickets();
    }
  }, []);

  const fetchProfileData = async () => {
    try {
      const response = await fetch('/api/profile', {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      if (response.ok) {
        const data = await response.json();
        setProfileData(data);
      }
    } catch (error) {
      console.error('Error fetching profile:', error);
    } finally {
      setLoading(false);
    }
  };

  const fetchNFTCount = async () => {
    try {
      const response = await fetch('https://ahantravyuh.com/api/nfts', {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      if (response.ok) {
        const data = await response.json();
        setNftCount(data.data?.nfts?.length || 0);
      }
    } catch (error) {
      console.error('Error fetching NFT count:', error);
    }
  };

  const fetchGarudLevel = async () => {
    try {
      const response = await fetch('https://ahantravyuh.com/garud/api/stats', {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      if (response.ok) {
        const data = await response.json();
        if (data.success && data.stats) setGarudLevel(data.stats.current_level || 0);
      }
    } catch (error) {
      console.error('Error fetching Garud level:', error);
    }
  };

  const fetchAnnouncements = async () => {
    try {
      const response = await fetch('/api/announcements', {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      if (response.ok) {
        const data = await response.json();
        setAnnouncements(data.announcements || []);
      }
    } catch (error) {
      console.error('Error fetching announcements:', error);
    }
  };

  const fetchAllAnnouncements = async () => {
    try {
      const response = await fetch('/api/admin/announcements', {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      if (response.ok) {
        const data = await response.json();
        setAllAnnouncements(data.announcements || []);
      }
    } catch (error) {
      console.error('Error fetching all announcements:', error);
    }
  };

  const fetchUsers = async () => {
    try {
      const response = await fetch('/api/admin/users-list', {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      if (response.ok) {
        const data = await response.json();
        setUsers(data.users || []);
      }
    } catch (error) {
      console.error('Error fetching users:', error);
    }
  };

  const fetchTickets = async () => {
    try {
      const response = await fetch('/api/tickets', {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      if (response.ok) {
        const data = await response.json();
        setTickets(data.tickets || []);
      }
    } catch (error) {
      console.error('Error fetching tickets:', error);
    }
  };

  const fetchAllTickets = async () => {
    try {
      const url = ticketFilter === 'all' ? '/api/admin/tickets' : `/api/admin/tickets?status=${ticketFilter}`;
      const response = await fetch(url, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      if (response.ok) {
        const data = await response.json();
        setAllTickets(data.tickets || []);
      }
    } catch (error) {
      console.error('Error fetching all tickets:', error);
    }
  };

  const fetchTicketDetails = async (ticketId: string) => {
    try {
      const response = await fetch(`/api/tickets/${ticketId}`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      if (response.ok) {
        const data = await response.json();
        setSelectedTicket(data.ticket);
        setTicketReplies(data.replies || []);
      }
    } catch (error) {
      console.error('Error fetching ticket details:', error);
    }
  };

  const createAnnouncement = async () => {
    setAnnouncementError('');
    try {
      const response = await fetch('/api/admin/announcements', {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          title: newAnnouncement.title,
          message: newAnnouncement.message,
          type: newAnnouncement.type,
          target_type: newAnnouncement.target_type,
          target_user_email: newAnnouncement.target_type === 'user' ? newAnnouncement.target_user_email : null,
          expires_at: newAnnouncement.expires_at || null
        })
      });
      const data = await response.json();
      if (response.ok) {
        setNewAnnouncement({ title: '', message: '', type: 'info', target_type: 'broadcast', target_user_email: '', expires_at: '' });
        fetchAnnouncements();
        fetchAllAnnouncements();
      } else {
        setAnnouncementError(data.message || 'Failed to create announcement');
      }
    } catch (error) {
      console.error('Error creating announcement:', error);
      setAnnouncementError('Failed to create announcement');
    }
  };

  const updateAnnouncement = async (id: string, updates: Partial<Announcement>) => {
    try {
      const response = await fetch(`/api/admin/announcements/${id}`, {
        method: 'PUT',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify(updates)
      });
      if (response.ok) {
        setEditingAnnouncement(null);
        fetchAnnouncements();
        fetchAllAnnouncements();
      }
    } catch (error) {
      console.error('Error updating announcement:', error);
    }
  };

  const deleteAnnouncement = async (id: string) => {
    if (!window.confirm('Delete this announcement?')) return;
    try {
      const response = await fetch(`/api/admin/announcements/${id}`, {
        method: 'DELETE',
        headers: { 'Authorization': `Bearer ${token}` }
      });
      if (response.ok) {
        fetchAnnouncements();
        fetchAllAnnouncements();
      }
    } catch (error) {
      console.error('Error deleting announcement:', error);
    }
  };

  const createTicket = async () => {
    try {
      const response = await fetch('/api/tickets', {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify(newTicket)
      });
      if (response.ok) {
        setNewTicket({ subject: '', category: 'general', priority: 'medium', message: '' });
        setShowNewTicketForm(false);
        fetchTickets();
        if (isAdmin) fetchAllTickets();
      }
    } catch (error) {
      console.error('Error creating ticket:', error);
    }
  };

  const addReply = async () => {
    if (!selectedTicket || !newReply.trim()) return;
    try {
      const response = await fetch(`/api/tickets/${selectedTicket.id}/reply`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({ message: newReply })
      });
      if (response.ok) {
        setNewReply('');
        fetchTicketDetails(selectedTicket.id);
        fetchTickets();
        if (isAdmin) fetchAllTickets();
      }
    } catch (error) {
      console.error('Error adding reply:', error);
    }
  };

  const updateTicketStatus = async (ticketId: string, status: string) => {
    try {
      const response = await fetch(`/api/tickets/${ticketId}/status`, {
        method: 'PUT',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({ status })
      });
      if (response.ok) {
        fetchTickets();
        if (isAdmin) fetchAllTickets();
        if (selectedTicket?.id === ticketId) {
          fetchTicketDetails(ticketId);
        }
      }
    } catch (error) {
      console.error('Error updating ticket status:', error);
    }
  };

  useEffect(() => {
    if (isAdmin) fetchAllTickets();
  }, [ticketFilter]);

  const getAnnouncementColor = (type: string) => {
    switch (type) {
      case 'success': return 'from-green-500/20 to-emerald-500/20 border-green-400/30';
      case 'warning': return 'from-yellow-500/20 to-orange-500/20 border-yellow-400/30';
      case 'promo': return 'from-pink-500/20 to-purple-500/20 border-pink-400/30';
      default: return 'from-blue-500/20 to-indigo-500/20 border-blue-400/30';
    }
  };

  const getStatusColor = (status: string) => {
    switch (status) {
      case 'open': return 'bg-blue-500/30 text-blue-300';
      case 'in_progress': return 'bg-yellow-500/30 text-yellow-300';
      case 'resolved': return 'bg-green-500/30 text-green-300';
      case 'closed': return 'bg-gray-500/30 text-gray-300';
      default: return 'bg-gray-500/30 text-gray-300';
    }
  };

  const getPriorityColor = (priority: string) => {
    switch (priority) {
      case 'urgent': return 'bg-red-500/30 text-red-300';
      case 'high': return 'bg-orange-500/30 text-orange-300';
      case 'medium': return 'bg-yellow-500/30 text-yellow-300';
      case 'low': return 'bg-green-500/30 text-green-300';
      default: return 'bg-gray-500/30 text-gray-300';
    }
  };

  const broadcastAnnouncements = announcements.filter(a => a.target_type === 'broadcast');
  const personalAnnouncements = announcements.filter(a => a.target_type === 'user');
  const displayTickets = isAdmin && showAdminPanel ? allTickets : tickets;

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <div className="text-white text-xl">Loading profile...</div>
      </div>
    );
  }

  return (
    <div className="min-h-screen p-6 pb-20">
      <div className="max-w-7xl mx-auto space-y-6">

        {/* Header */}
        <div className="bg-white bg-opacity-10 backdrop-blur-lg rounded-2xl p-6 border border-white border-opacity-20">
          <div className="flex items-center justify-between flex-wrap gap-4">
            <div className="flex items-center space-x-4">
              <div className="w-20 h-20 bg-gradient-to-br from-purple-500 to-blue-500 rounded-full flex items-center justify-center">
                <User className="w-10 h-10 text-white" />
              </div>
              <div>
                <h1 className="text-3xl font-bold text-white">{user?.name}</h1>
                <p className="text-gray-300">{user?.email}</p>
                <p className="text-sm text-gray-400 mt-1">
                  Member since {new Date(profileData?.member_since || Date.now()).toLocaleDateString()}
                </p>
              </div>
            </div>
            {isAdmin && (
              <button
                onClick={() => setShowAdminPanel(!showAdminPanel)}
                className={`flex items-center space-x-2 px-4 py-2 rounded-lg transition-all ${
                  showAdminPanel ? 'bg-purple-500 text-white' : 'bg-white/10 text-white hover:bg-white/20'
                }`}
              >
                <Shield className="w-5 h-5" />
                <span>Admin Panel</span>
              </button>
            )}
          </div>
        </div>

        {/* Admin Panel */}
        {isAdmin && showAdminPanel && (
          <div className="bg-gradient-to-r from-purple-500/20 to-indigo-500/20 backdrop-blur-lg rounded-2xl p-6 border border-purple-400/30">
            <h2 className="text-2xl font-bold text-white mb-6 flex items-center">
              <Shield className="w-6 h-6 mr-2" />
              Admin Panel
            </h2>

            {/* Announcement Management */}
            <div className="mb-8">
              <h3 className="text-xl font-semibold text-white mb-4">Announcement Management</h3>
              <div className="bg-white/10 rounded-xl p-4 mb-4">
                <h4 className="text-sm font-medium text-gray-300 mb-3">Create New Announcement</h4>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                  <input type="text" placeholder="Title" value={newAnnouncement.title}
                    onChange={(e) => setNewAnnouncement({...newAnnouncement, title: e.target.value})}
                    className="px-3 py-2 bg-white/10 border border-white/20 rounded-lg text-white text-sm placeholder-gray-400" />
                  <select value={newAnnouncement.type}
                    onChange={(e) => setNewAnnouncement({...newAnnouncement, type: e.target.value as any})}
                    className="px-3 py-2 bg-white/10 border border-white/20 rounded-lg text-white text-sm">
                    <option value="info" className="bg-gray-800">Info</option>
                    <option value="success" className="bg-gray-800">Success</option>
                    <option value="warning" className="bg-gray-800">Warning</option>
                    <option value="promo" className="bg-gray-800">Promo</option>
                  </select>
                  <textarea placeholder="Message" value={newAnnouncement.message}
                    onChange={(e) => setNewAnnouncement({...newAnnouncement, message: e.target.value})}
                    className="md:col-span-2 px-3 py-2 bg-white/10 border border-white/20 rounded-lg text-white text-sm placeholder-gray-400 min-h-[80px]" />
                  <div className="flex items-center space-x-4">
                    <label className="flex items-center space-x-2 text-white text-sm cursor-pointer">
                      <input type="radio" checked={newAnnouncement.target_type === 'broadcast'}
                        onChange={() => setNewAnnouncement({...newAnnouncement, target_type: 'broadcast', target_user_email: ''})} />
                      <Globe className="w-4 h-4" /><span>All Users</span>
                    </label>
                    <label className="flex items-center space-x-2 text-white text-sm cursor-pointer">
                      <input type="radio" checked={newAnnouncement.target_type === 'user'}
                        onChange={() => setNewAnnouncement({...newAnnouncement, target_type: 'user'})} />
                      <UserCircle className="w-4 h-4" /><span>Specific User</span>
                    </label>
                  </div>
                  {newAnnouncement.target_type === 'user' && (
                    <input type="email" placeholder="Enter user's email address"
                      value={newAnnouncement.target_user_email}
                      onChange={(e) => setNewAnnouncement({...newAnnouncement, target_user_email: e.target.value})}
                      className="px-3 py-2 bg-white/10 border border-white/20 rounded-lg text-white text-sm placeholder-gray-400" />
                  )}
                  {announcementError && (
                    <div className="md:col-span-2 text-red-400 text-sm">{announcementError}</div>
                  )}
                </div>
                <button onClick={createAnnouncement}
                  disabled={!newAnnouncement.title || !newAnnouncement.message}
                  className="mt-3 px-4 py-2 bg-purple-500 text-white rounded-lg text-sm font-medium hover:bg-purple-600 disabled:opacity-50">
                  Create Announcement
                </button>
              </div>
              <div className="space-y-2 max-h-60 overflow-y-auto">
                {allAnnouncements.map(ann => (
                  <div key={ann.id} className={`p-3 rounded-lg border text-sm ${ann.is_active ? 'bg-white/10 border-white/20' : 'bg-gray-500/20 border-gray-500/30'}`}>
                    <div className="flex items-center justify-between">
                      <div className="flex items-center space-x-2">
                        {ann.target_type === 'broadcast' ? <Globe className="w-4 h-4 text-blue-400" /> : <UserCircle className="w-4 h-4 text-green-400" />}
                        <span className="text-white font-medium">{ann.title}</span>
                      </div>
                      <div className="flex space-x-1">
                        <button onClick={() => updateAnnouncement(ann.id, { is_active: !ann.is_active })}
                          className={`p-1 rounded ${ann.is_active ? 'text-yellow-400' : 'text-green-400'}`}>
                          {ann.is_active ? <X className="w-4 h-4" /> : <Bell className="w-4 h-4" />}
                        </button>
                        <button onClick={() => deleteAnnouncement(ann.id)} className="p-1 text-red-400">
                          <Trash2 className="w-4 h-4" />
                        </button>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            </div>

            {/* Ticket Management (Admin) */}
            <div>
              <h3 className="text-xl font-semibold text-white mb-4">Support Tickets</h3>
              <div className="flex space-x-2 mb-4">
                {['all', 'open', 'in_progress', 'resolved', 'closed'].map(status => (
                  <button key={status} onClick={() => setTicketFilter(status)}
                    className={`px-3 py-1 rounded-lg text-sm ${ticketFilter === status ? 'bg-purple-500 text-white' : 'bg-white/10 text-gray-300 hover:bg-white/20'}`}>
                    {status === 'all' ? 'All' : status.replace('_', ' ').replace(/\b\w/g, l => l.toUpperCase())}
                  </button>
                ))}
              </div>
              <div className="space-y-2 max-h-80 overflow-y-auto">
                {allTickets.map(ticket => (
                  <div key={ticket.id}
                    onClick={() => fetchTicketDetails(ticket.id)}
                    className={`p-4 rounded-xl border cursor-pointer transition-all ${selectedTicket?.id === ticket.id ? 'bg-purple-500/20 border-purple-400/50' : 'bg-white/10 border-white/20 hover:bg-white/15'}`}>
                    <div className="flex items-center justify-between mb-2">
                      <span className="text-white font-medium">{ticket.subject}</span>
                      <div className="flex space-x-2">
                        <span className={`text-xs px-2 py-0.5 rounded ${getPriorityColor(ticket.priority)}`}>{ticket.priority}</span>
                        <span className={`text-xs px-2 py-0.5 rounded ${getStatusColor(ticket.status)}`}>{ticket.status}</span>
                      </div>
                    </div>
                    <div className="flex items-center justify-between text-xs text-gray-400">
                      <span>From: {ticket.user_name} ({ticket.user_email})</span>
                      <span>{ticket.reply_count} replies</span>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </div>
        )}

        {/* Wallet Overview */}
        <div>
          <h2 className="text-2xl font-bold text-white mb-4 flex items-center">
            <Wallet className="w-6 h-6 mr-2" />
            Your Wallet
          </h2>
          <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
            <div className="bg-gradient-to-br from-yellow-500/20 to-orange-500/20 rounded-xl p-4 border border-yellow-400/30">
              <Zap className="w-6 h-6 text-yellow-400 mb-2" />
              <div className="text-2xl font-bold text-white">{user?.urja_balance || 0}</div>
              <div className="text-sm text-gray-300">URJA</div>
            </div>
            <div className="bg-gradient-to-br from-amber-500/20 to-orange-500/20 rounded-xl p-4 border border-amber-400/30">
              <Coins className="w-6 h-6 text-amber-400 mb-2" />
              <div className="text-2xl font-bold text-white">{user?.ahant_balance || 0}</div>
              <div className="text-sm text-gray-300">AHANT</div>
            </div>
            <div className="bg-gradient-to-br from-blue-500/20 to-cyan-500/20 rounded-xl p-4 border border-blue-400/30">
              <Droplets className="w-6 h-6 text-blue-400 mb-2" />
              <div className="text-2xl font-bold text-white">{profileData?.hydrogen_balance?.toFixed(4) || '0.0000'}</div>
              <div className="text-sm text-gray-300">Hydrogen</div>
            </div>
            <div className="bg-gradient-to-br from-green-500/20 to-emerald-500/20 rounded-xl p-4 border border-green-400/30">
              <Leaf className="w-6 h-6 text-green-400 mb-2" />
              <div className="text-2xl font-bold text-white">{profileData?.rice_balance?.toFixed(4) || '0.0000'}</div>
              <div className="text-sm text-gray-300">Rice (KG)</div>
            </div>
          </div>
        </div>

        {/* Stats & Quick Actions */}
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
          <div className="bg-white bg-opacity-10 rounded-2xl p-6 border border-white/20">
            <h3 className="text-xl font-bold text-white mb-4 flex items-center">
              <TrendingUp className="w-5 h-5 mr-2" />
              Activity
            </h3>
            <div className="space-y-3">
              <div className="flex items-center justify-between p-3 bg-white/5 rounded-lg">
                <div className="flex items-center space-x-3"><Package className="w-5 h-5 text-purple-400" /><span className="text-white">NFTs</span></div>
                <span className="text-xl font-bold text-purple-400">{nftCount}</span>
              </div>
              <div className="flex items-center justify-between p-3 bg-white/5 rounded-lg">
                <div className="flex items-center space-x-3"><Droplets className="w-5 h-5 text-blue-400" /><span className="text-white">Vehicles</span></div>
                <span className="text-xl font-bold text-blue-400">{profileData?.total_vehicles || 0}</span>
              </div>
              <div className="flex items-center justify-between p-3 bg-white/5 rounded-lg">
                <div className="flex items-center space-x-3"><Bot className="w-5 h-5 text-red-400" /><span className="text-white">Garud Level</span></div>
                <span className="text-xl font-bold text-red-400">{garudLevel}</span>
              </div>
            </div>
          </div>
          <div className="bg-white bg-opacity-10 rounded-2xl p-6 border border-white/20">
            <h3 className="text-xl font-bold text-white mb-4 flex items-center">
              <Award className="w-5 h-5 mr-2" />
              Quick Actions
            </h3>
            <div className="grid grid-cols-2 gap-3">
              <button onClick={() => window.location.href = '/atavi/'} className="p-3 bg-green-500/20 rounded-xl border border-green-400/30 text-white text-sm font-medium hover:bg-green-500/30">
                <Leaf className="w-5 h-5 mx-auto mb-1" />Plant Trees
              </button>
              <button onClick={() => window.location.href = '/waahan/'} className="p-3 bg-blue-500/20 rounded-xl border border-blue-400/30 text-white text-sm font-medium hover:bg-blue-500/30">
                <Droplets className="w-5 h-5 mx-auto mb-1" />Waahan
              </button>
              <button onClick={() => window.location.href = '/ahant/'} className="p-3 bg-amber-500/20 rounded-xl border border-amber-400/30 text-white text-sm font-medium hover:bg-amber-500/30">
                <Coins className="w-5 h-5 mx-auto mb-1" />Trade
              </button>
              <button onClick={() => window.location.href = '/garud/'} className="p-3 bg-red-500/20 rounded-xl border border-red-400/30 text-white text-sm font-medium hover:bg-red-500/30">
                <Bot className="w-5 h-5 mx-auto mb-1" />Battle
              </button>
            </div>
          </div>
        </div>

        {/* Support Tickets Section */}
        <div className="bg-white bg-opacity-10 rounded-2xl p-6 border border-white/20">
          <div className="flex items-center justify-between mb-4">
            <h2 className="text-2xl font-bold text-white flex items-center">
              <Ticket className="w-6 h-6 mr-2" />
              Support Tickets
            </h2>
            <button onClick={() => setShowNewTicketForm(!showNewTicketForm)}
              className="flex items-center space-x-2 px-4 py-2 bg-blue-500 text-white rounded-lg text-sm font-medium hover:bg-blue-600">
              <Plus className="w-4 h-4" />
              <span>New Ticket</span>
            </button>
          </div>

          {/* New Ticket Form */}
          {showNewTicketForm && (
            <div className="bg-white/10 rounded-xl p-4 mb-4 border border-blue-400/30">
              <h3 className="text-lg font-semibold text-white mb-3">Create New Ticket</h3>
              <div className="space-y-3">
                <input type="text" placeholder="Subject" value={newTicket.subject}
                  onChange={(e) => setNewTicket({...newTicket, subject: e.target.value})}
                  className="w-full px-4 py-2 bg-white/10 border border-white/20 rounded-lg text-white placeholder-gray-400" />
                <div className="grid grid-cols-2 gap-3">
                  <select value={newTicket.category}
                    onChange={(e) => setNewTicket({...newTicket, category: e.target.value})}
                    className="px-4 py-2 bg-white/10 border border-white/20 rounded-lg text-white">
                    <option value="general" className="bg-gray-800">General</option>
                    <option value="bug" className="bg-gray-800">Bug Report</option>
                    <option value="feature" className="bg-gray-800">Feature Request</option>
                    <option value="billing" className="bg-gray-800">Billing</option>
                    <option value="account" className="bg-gray-800">Account</option>
                    <option value="other" className="bg-gray-800">Other</option>
                  </select>
                  <select value={newTicket.priority}
                    onChange={(e) => setNewTicket({...newTicket, priority: e.target.value})}
                    className="px-4 py-2 bg-white/10 border border-white/20 rounded-lg text-white">
                    <option value="low" className="bg-gray-800">Low Priority</option>
                    <option value="medium" className="bg-gray-800">Medium Priority</option>
                    <option value="high" className="bg-gray-800">High Priority</option>
                    <option value="urgent" className="bg-gray-800">Urgent</option>
                  </select>
                </div>
                <textarea placeholder="Describe your issue..." value={newTicket.message}
                  onChange={(e) => setNewTicket({...newTicket, message: e.target.value})}
                  className="w-full px-4 py-2 bg-white/10 border border-white/20 rounded-lg text-white placeholder-gray-400 min-h-[100px]" />
                <div className="flex space-x-3">
                  <button onClick={createTicket}
                    disabled={!newTicket.subject || !newTicket.message}
                    className="px-6 py-2 bg-blue-500 text-white rounded-lg font-medium hover:bg-blue-600 disabled:opacity-50">
                    Submit Ticket
                  </button>
                  <button onClick={() => setShowNewTicketForm(false)}
                    className="px-6 py-2 bg-gray-500 text-white rounded-lg font-medium hover:bg-gray-600">
                    Cancel
                  </button>
                </div>
              </div>
            </div>
          )}

          {/* Tickets List */}
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
            <div className="space-y-2">
              <h4 className="text-sm font-medium text-gray-400 mb-2">Your Tickets ({tickets.length})</h4>
              {tickets.length === 0 ? (
                <div className="text-center py-8 text-gray-400">
                  <Ticket className="w-12 h-12 mx-auto mb-2 opacity-50" />
                  <p>No tickets yet</p>
                </div>
              ) : (
                <div className="space-y-2 max-h-80 overflow-y-auto">
                  {tickets.map(ticket => (
                    <div key={ticket.id}
                      onClick={() => fetchTicketDetails(ticket.id)}
                      className={`p-4 rounded-xl border cursor-pointer transition-all ${selectedTicket?.id === ticket.id ? 'bg-blue-500/20 border-blue-400/50' : 'bg-white/5 border-white/10 hover:bg-white/10'}`}>
                      <div className="flex items-center justify-between mb-1">
                        <span className="text-white font-medium text-sm truncate">{ticket.subject}</span>
                        <span className={`text-xs px-2 py-0.5 rounded ${getStatusColor(ticket.status)}`}>{ticket.status}</span>
                      </div>
                      <div className="flex items-center justify-between text-xs text-gray-400">
                        <span>{ticket.category}</span>
                        <span>{new Date(ticket.created_at).toLocaleDateString()}</span>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>

            {/* Ticket Details */}
            <div>
              {selectedTicket ? (
                <div className="bg-white/5 rounded-xl p-4 border border-white/10">
                  <div className="flex items-center justify-between mb-4">
                    <h4 className="text-lg font-semibold text-white">{selectedTicket.subject}</h4>
                    <button onClick={() => setSelectedTicket(null)} className="text-gray-400 hover:text-white">
                      <X className="w-5 h-5" />
                    </button>
                  </div>
                  <div className="flex space-x-2 mb-4">
                    <span className={`text-xs px-2 py-1 rounded ${getStatusColor(selectedTicket.status)}`}>{selectedTicket.status}</span>
                    <span className={`text-xs px-2 py-1 rounded ${getPriorityColor(selectedTicket.priority)}`}>{selectedTicket.priority}</span>
                    <span className="text-xs px-2 py-1 rounded bg-gray-500/30 text-gray-300">{selectedTicket.category}</span>
                  </div>

                  {/* Status buttons */}
                  {(isAdmin || selectedTicket.status !== 'closed') && (
                    <div className="flex flex-wrap gap-2 mb-4">
                      {isAdmin ? (
                        <>
                          <button onClick={() => updateTicketStatus(selectedTicket.id, 'in_progress')}
                            className="text-xs px-3 py-1 bg-yellow-500/20 text-yellow-300 rounded hover:bg-yellow-500/30">In Progress</button>
                          <button onClick={() => updateTicketStatus(selectedTicket.id, 'resolved')}
                            className="text-xs px-3 py-1 bg-green-500/20 text-green-300 rounded hover:bg-green-500/30">Resolved</button>
                          <button onClick={() => updateTicketStatus(selectedTicket.id, 'closed')}
                            className="text-xs px-3 py-1 bg-gray-500/20 text-gray-300 rounded hover:bg-gray-500/30">Close</button>
                        </>
                      ) : (
                        <button onClick={() => updateTicketStatus(selectedTicket.id, 'closed')}
                          className="text-xs px-3 py-1 bg-gray-500/20 text-gray-300 rounded hover:bg-gray-500/30">Close Ticket</button>
                      )}
                    </div>
                  )}

                  {/* Replies */}
                  <div className="space-y-3 max-h-60 overflow-y-auto mb-4">
                    {ticketReplies.map(reply => (
                      <div key={reply.id}
                        className={`p-3 rounded-lg ${reply.is_admin_reply ? 'bg-purple-500/20 border-l-2 border-purple-500' : 'bg-white/10'}`}>
                        <div className="flex items-center justify-between mb-1">
                          <span className={`text-sm font-medium ${reply.is_admin_reply ? 'text-purple-300' : 'text-white'}`}>
                            {reply.user_name} {reply.is_admin_reply && '(Admin)'}
                          </span>
                          <span className="text-xs text-gray-400">{new Date(reply.created_at).toLocaleString()}</span>
                        </div>
                        <p className="text-gray-200 text-sm whitespace-pre-wrap">{reply.message}</p>
                      </div>
                    ))}
                  </div>

                  {/* Reply Form */}
                  {selectedTicket.status !== 'closed' && (
                    <div className="flex space-x-2">
                      <input type="text" placeholder="Type your reply..."
                        value={newReply} onChange={(e) => setNewReply(e.target.value)}
                        onKeyPress={(e) => e.key === 'Enter' && addReply()}
                        className="flex-1 px-4 py-2 bg-white/10 border border-white/20 rounded-lg text-white placeholder-gray-400 text-sm" />
                      <button onClick={addReply}
                        disabled={!newReply.trim()}
                        className="px-4 py-2 bg-blue-500 text-white rounded-lg hover:bg-blue-600 disabled:opacity-50">
                        <Send className="w-4 h-4" />
                      </button>
                    </div>
                  )}
                </div>
              ) : (
                <div className="bg-white/5 rounded-xl p-8 border border-white/10 text-center">
                  <MessageSquare className="w-12 h-12 mx-auto text-gray-500 mb-2" />
                  <p className="text-gray-400">Select a ticket to view details</p>
                </div>
              )}
            </div>
          </div>
        </div>

        {/* Personal Announcements */}
        {personalAnnouncements.length > 0 && (
          <div>
            <h2 className="text-2xl font-bold text-white mb-4 flex items-center">
              <UserCircle className="w-6 h-6 mr-2 text-green-400" />
              Messages For You
            </h2>
            <div className="space-y-4">
              {personalAnnouncements.map(ann => (
                <div key={ann.id} className={`bg-gradient-to-r ${getAnnouncementColor(ann.type)} rounded-xl p-6 border relative`}>
                  <span className="absolute top-3 right-3 text-xs px-2 py-1 bg-green-500/30 text-green-300 rounded">Personal</span>
                  <div className="flex items-center space-x-2 mb-2">
                    <Bell className="w-5 h-5 text-white" />
                    <h3 className="text-lg font-bold text-white">{ann.title}</h3>
                  </div>
                  <p className="text-gray-200 mb-2">{ann.message}</p>
                  <p className="text-xs text-gray-400">{new Date(ann.created_at).toLocaleDateString()}</p>
                </div>
              ))}
            </div>
          </div>
        )}

        {/* Broadcast Announcements */}
        <div>
          <h2 className="text-2xl font-bold text-white mb-4 flex items-center">
            <Megaphone className="w-6 h-6 mr-2" />
            Announcements
          </h2>
          {broadcastAnnouncements.length > 0 ? (
            <div className="space-y-4">
              {broadcastAnnouncements.map(ann => (
                <div key={ann.id} className={`bg-gradient-to-r ${getAnnouncementColor(ann.type)} rounded-xl p-6 border`}>
                  <div className="flex items-center space-x-2 mb-2">
                    <Bell className="w-5 h-5 text-white" />
                    <h3 className="text-lg font-bold text-white">{ann.title}</h3>
                  </div>
                  <p className="text-gray-200 mb-2">{ann.message}</p>
                  <p className="text-xs text-gray-400">{new Date(ann.created_at).toLocaleDateString()}</p>
                </div>
              ))}
            </div>
          ) : (
            <div className="bg-white/10 rounded-xl p-6 border border-white/20 text-center">
              <Megaphone className="w-12 h-12 text-gray-400 mx-auto mb-3" />
              <p className="text-gray-400">No announcements</p>
            </div>
          )}
        </div>

        {/* Referral Links */}
        <div>
          <h2 className="text-2xl font-bold text-white mb-4 flex items-center">
            <Coins className="w-6 h-6 mr-2" />
            Earn Free AHANT
          </h2>
          <div className="bg-gradient-to-r from-amber-500/20 to-orange-500/20 rounded-xl p-6 border border-amber-400/30">
            <p className="text-gray-200 mb-4">Support AhantraVyuh and earn free AHANT tokens through our referral links!</p>
            <a href="/referrals.html" target="_blank" rel="noopener noreferrer"
              className="inline-flex items-center space-x-2 bg-gradient-to-r from-amber-500 to-orange-500 text-white px-6 py-3 rounded-lg font-semibold hover:from-amber-600 hover:to-orange-600">
              <Coins className="w-5 h-5" />
              <span>View Referral Links</span>
            </a>
          </div>
        </div>

      </div>
    </div>
  );
};

export default Profile;
